import { router } from '../../service/router';
import { themeLoaderService } from '../../service/theme-loader';
import { appConfig, UseCase } from '../../service/config';
import { translations } from '../../service/i18n';
import { fullSizeService } from '../../service/full-size';
import { messageService } from '../../service/message';
import { thirdPartySupportService } from './third-party-support.service';
import { APP_TYPES } from './navigator/constants';
import * as Logger from '@navigator/frontend-logger';

// interfaces
import {
  ApplicationLifeCycle,
  InterruptionInterface,
  KeepAlive as KeepAliveInterface,
  MonitoringLoggingQueue,
  NavigatorInterface,
  PerformanceTracking as PerformanceInterface,
  Theme as ThemeInterface,
  ToolbarInterface,
  BannerInterface,
} from './navigator/interface';
// main actors of old NavigatorJS
import { Config, IACCommunication, IACHub, Navigator } from './navigator';
import { BaseApplication, DialogApplication, FullScreenApplication } from './navigator/application';

export class ThirdPartyMediatorService {

  logoutRequested = false;

  constructor() {
    // set mediator service to all interfaces
    ApplicationLifeCycle.mediator = this;
    InterruptionInterface.mediator = this;
    KeepAliveInterface.mediator = this;
    MonitoringLoggingQueue.mediator = this;
    NavigatorInterface.mediator = this;
    PerformanceInterface.mediator = this;
    ThemeInterface.mediator = this;
    ToolbarInterface.mediator = this;
    BannerInterface.mediator = this;
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#init
   *
   * @description
   * Initializes the Navigator implementation.
   */
  public init(): void {
    // listen route changes to call the corresponding IAC Usecase if necessary
    router.on('change', () => {
      const appName = window.location.pathname.replace(/^\//, '').split('?')[0].split('(')[0];

      // application will be in focus if it was opened through a IAC Usecase
      const app = Navigator.getApplication(appName);

      if (!app && !this.isStartupApplied()) {
        this.handleStartup();
      }

      if (app && !app.isOnFocus()) {
        // call open usecase to report the navigator logic an app was opened
        IACHub.handle(app.getInitialUsecase());
      }
    });

    Config.init(appConfig);
    IACCommunication.init();
    Navigator.init();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#initApplication
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to initialize an application
   *
   * @param app {BaseApplication} Application instance
   */
  initApplication(app: BaseApplication): void {
    const appName = app.getSysname();

    thirdPartySupportService.initApplication(appName);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#focusApplication
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to bring an application to the front
   *
   * @param app {BaseApplication} Application instance
   */
  focusApplication(app: BaseApplication): void {
    const appName = app.getSysname();

    // set route only for "normal" fullscreen application
    if (app.type === APP_TYPES.FULLSCREEN) {
      thirdPartySupportService.activate(appName);
      router.navigateTo(appName);
    } else {
      thirdPartySupportService.initDialog(appName, !app.config.wait_for_application_ready &&
        !app.config.start_in_background);
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#unFocusApplication
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to bring an application into the background
   *
   * @param app {BaseApplication} Application instance
   */
  unFocusApplication(app: BaseApplication): void {
    const appName = app.getSysname();

    // Only full applications can send into the background
    if (app.type === APP_TYPES.FULLSCREEN) {
      thirdPartySupportService.deactivate(appName);
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#reInitApplication
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to re initialize the application (reload)
   *
   * @param app {BaseApplication} Application instance
   * @param urlKey {String} Key of URL map of the application, commonly set as 'default'
   */
  reInitApplication(app: BaseApplication, urlKey: string): void {
    thirdPartySupportService.reloadApplication(app.getSysname(), urlKey);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#handleStartup
   *
   * @description
   * Run the defined navigator startup use cases, but only once. To check if
   * the startup is already done use ThirdPartyMediator#isStartupApplied.
   */
  handleStartup(): void {
    Navigator.handleStartup();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#isStartupApplied
   *
   * @description
   * Run true when the startup use cases are already applied.
   */
  isStartupApplied(): boolean {
    return Navigator.isStartupApplied();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#closeDialogApplication
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to close a dialog application
   *
   * @param app {BaseApplication} Application instance
   */
  closeDialogApplication(app: BaseApplication): void {
    const appName = app.getSysname();

    thirdPartySupportService.deactivateDialog(appName);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#activateDialogApplication
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to activate a dialog application when it triggers the
   * application_ready use case
   *
   * @param app {BaseApplication} Application instance
   */
  activateDialogApplication(app: DialogApplication): void {
    const appName = app.getSysname();

    thirdPartySupportService.activateDialog(appName);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#isApplicationInitialized
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to check if an application
   * is already initialized
   *
   * @param app {BaseApplication} Application instance
   *
   * @returns {Boolean} True if application is already initialized
   */
  isApplicationInitialized(app: BaseApplication): boolean {
    const appName = app.getSysname();

    return !!thirdPartySupportService.initializedApps
      .find((initApp) => initApp.name === appName);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#isApplicationOnFocus
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to check if an fullscreen (normal)
   * application is currently in front
   *
   * @param app {FullScreenApplication} FullScreen (normal) Application instance
   *
   * @returns {Boolean} True if application is already in focus
   */
  isApplicationOnFocus(app: FullScreenApplication): boolean {
    const appName = app.getSysname();

    return !!thirdPartySupportService.initializedApps
      .find((initApp) => initApp.name === appName && initApp.active === true);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#isDialogActive
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to check if
   * an dialog application is currently in front
   *
   * @param app {DialogApplication} Dialog application instance
   *
   * @returns {Boolean} True if application is already in focus
   */
  isDialogOnFocus(app: DialogApplication): boolean {
    return !!(thirdPartySupportService.modalDialogApp &&
      thirdPartySupportService.modalDialogApp.name === app.getSysname() &&
      thirdPartySupportService.modalDialogApp.ready);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#isDialogInitialized
   *
   * @description
   * Will be called by ApplicationLifeCycle interface to check if a dialog
   * is already initialized
   *
   * @param app {DialogApplication} Dialog application instance
   *
   * @returns {Boolean} True if application is already initialized
   */
  isDialogInitialized(app: DialogApplication): boolean {
    return !!(thirdPartySupportService.modalDialogApp &&
      thirdPartySupportService.modalDialogApp.name === app.getSysname());
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#isFeedbackButtonVisible
   *
   * @description
   * Determines if currently focused application is enabled for feedback.
   * Will be called by @poseidon/component/feedback/Feedback directive
   *
   * @returns {Boolean} True if currently focused application is enabled for feedback
   */
  isFeedbackButtonVisible(): boolean {
    const app = Navigator.getFocusedApplication();

    return !!app && app.hasFeedback();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#openFeedbackApplication
   *
   * @description
   * Opens feedback dialog.
   * Will be called by @poseidon/component/feedback/Feedback directive
   */
  openFeedbackApplication(): void {
    Navigator.openFeedBack();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#resumeFocusedApp
   *
   * @description
   * Triggers the 'onresumefocus' IAC usecase if the given sysname is the
   * sysname of the focused app.
   */
  resumeFocusedApp(sysname: string): void {
    Navigator.resumeFocusedApp(sysname);
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#closeDialog
   *
   * @param {string} appName The name off the dialog to close.
   *
   * @description
   * Close the dialog and sends the 'onenabled' and 'onclose' IAC events.
   */
  closeDialog(appName: string): void {
    Navigator.closeDialog(appName);
  }

  handleUseCase(useCase: UseCase): void {
    IACHub.handle(IACCommunication.createUsecase(useCase.name, useCase.payload));
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#hideNavBar
   *
   * @description
   * Hides the navigation bar.
   * Will be called by navigator interface.
   */
  hideNavBar(): void {
    fullSizeService.fullSize = true;
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#showNavBar
   *
   * @description
   * Hides the navigation bar.
   * Will be called by navigator interface.
   */
  showNavBar(): void {
    fullSizeService.fullSize = false;
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#setActiveTheme
   *
   * @param {string} themeName Theme name to set as active
   *
   * @description
   * Sets the active theme provided by the Navigator at theme service
   */
  setActiveTheme(themeName: string): void {
    themeLoaderService.activeTheme = themeName;
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#logout
   *
   * @description
   * Call the logout on navigator interface
   */
  logout(): void {
    if (this.logoutRequested) {
      window.setTimeout(() => {
        this.logoutRequested = false;
      }, 3000);
    } else {
      this.logoutRequested = true;
      Navigator.handleLogout();
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#hideLastMessage
   *
   * @description
   * Cancel last Message
   *
   */
  hideLastMessage(): void {
    messageService.cancelLastMessage();
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#showConnectionLostMessage
   *
   * @description
   * Shows Connection Lost Message
   *
   */
  showConnectionLostMessage(): void {
    if (translations.OFFLINE_TITLE || translations.OFFLINE_TEXT) {
      messageService.showWarning({
        heading: translations.OFFLINE_TITLE,
        detailsHtml: translations.OFFLINE_TEXT,
      });
    } else {
      Logger.error('ThirdPartyMediatorService: Missing translations for offline message!');
    }
  }

  /*
   * @type method
   * @name @poseidon/third-party-support/ThirdPartyMediator#trackMark
   *
   * @description
   * Starts the trackMark by identifier.
   *
   */
  trackMark(id: string): void {
    PerformanceInterface.start(id);
  }

  showBanner(): void {
    thirdPartySupportService.showBanner();
  }

  hideBanner(): void {
    thirdPartySupportService.hideBanner();
  }

}

export const thirdPartyMediatorService = new ThirdPartyMediatorService();
